import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
// import { BaseFieldProps } from '@/unity-apps/mock-data/filters/page-filter/types-v2'
import { BaseFieldProps, StatusIndicatorConfig } from './types'
import { computeStatusColorForValue, statusColorMap } from './statusIndicatorUtils'
import type { ControllerRenderProps, FieldValues } from 'react-hook-form'
import clsx from 'clsx'
import { useMemo } from 'react'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { IconInformationOutline } from '@icons/IconInformationOutline'
import { DocumentationButton } from './DocumentationButton'

export const NumberField: React.FC<BaseFieldProps> = ({ field, formFieldName, control, variant }) => {
  return (
    <FormField
      control={control}
      name={formFieldName}
      render={({ field: formField }) => <NumberFieldInner field={field} formField={formField} variant={variant} />}
    />
  )
}

const NumberFieldInner: React.FC<{
  field: BaseFieldProps['field']
  formField: ControllerRenderProps<FieldValues, string>
  variant: BaseFieldProps['variant']
}> = ({ field, formField, variant }) => {
  const isCompact = variant === 'row' || variant === 'inline'

  const statusColor = useMemo(
    () =>
      computeStatusColorForValue(
        formField.value as number,
        field.statusIndicator as StatusIndicatorConfig | undefined,
        field.status
      ),
    [formField.value, field.statusIndicator, field.status]
  )

  const fieldControl = (
    <FormControl>
      <div className="relative">
        <Input
          type="number"
          {...formField}
          value={formField.value as number}
          onChange={(e) => formField.onChange(e.target.valueAsNumber)}
          disabled={field.readOnly}
          step={field.step}
          className={clsx(statusColor ? statusColorMap[statusColor] : '', {
            'h-6 w-20': isCompact,
            'border-none': variant === 'inline',
          })}
        />
      </div>
    </FormControl>
  )

  return (
    <FormItem
      className={clsx('flex flex-1 flex-col', {
        hidden: field.widget === 'hidden',
        'flex-row items-center': variant === 'row',
        'space-y-0': variant === 'inline',
      })}
    >
      <div className={clsx('flex justify-start gap-2', { hidden: variant === 'inline' })}>
        <FormLabel className={clsx({ 'sr-only': variant === 'row' })}>{field.title}</FormLabel>
        {!isCompact && <DocumentationButton documentation={field.documentation} />}
      </div>
      <FormDescription className={clsx('text-xs', { 'sr-only': variant === 'row', hidden: variant === 'inline' })}>
        {field.description}
      </FormDescription>
      {variant === 'row' ? (
        <div className="!mt-0 flex items-center gap-x-1 text-muted-foreground">
          <TooltipButton tooltip={field.title}>
            <div className="shrink-0">
              <IconInformationOutline className="size-4" />
            </div>
          </TooltipButton>
          {fieldControl}
        </div>
      ) : (
        fieldControl
      )}
      {!isCompact && <FormMessage />}
    </FormItem>
  )
}
